import { useEffect, useMemo, useState } from "react";
import { ArrowLeftRight, ArrowRight, Columns2, GitCompare, Rows3, Search, Table2 } from "lucide-react";
import { Alert, Badge, Button, EmptyState, Input, Select } from "@/components/ui";
import { useContextApi, isAbortError, contextErrorMessage, type DiffEntry, type TableCard, type Version } from "@/lib/contextApi";
import { cn } from "@/lib/utils";
import { SmartDiff, type DiffMode } from "./LineDiff";
import { formatDateTime, useErrorHandler } from "./shared";

/** Short, unique handle for a version: the hash suffix of its id. */
export const shortId = (v: string) => v.split("-").pop() ?? v;

export function versionLabel(v: Version) {
  return `${formatDateTime(v.updated_at)} · ${shortId(v.version)}${v.live ? " · LIVE" : ""} · ${v.note ?? ""}`;
}

type Kind = "changed" | "added" | "removed";
const kindOf = (e: DiffEntry): Kind => (e.before === "absent" || e.before === null || e.before === "" ? "added" : e.after === "absent" || e.after === null || e.after === "" ? "removed" : "changed");
const KIND_LABEL: Record<Kind, string> = { changed: "Gewijzigd", added: "Toegevoegd", removed: "Verwijderd" };
const KIND_TONE: Record<Kind, "neutral" | "success" | "danger"> = { changed: "neutral", added: "success", removed: "danger" };

const FIELD_LABEL: Record<string, string> = {
  purpose: "Doel",
  grain: "Granulariteit",
  synonyms: "Synoniemen",
  usage_guidance: "Gebruiksadvies",
  caveats: "Valkuilen",
  recipes: "Recepten",
  joins: "Joins",
  description: "Beschrijving",
  meaning: "Betekenis",
  usage: "Gebruik",
  role: "Rol",
  business_process: "Bedrijfsproces",
  capabilities: "Mogelijkheden",
  review: "Review-metadata",
  "(card)": "Hele kaart",
};
const fieldLabel = (f: string) => FIELD_LABEL[f] ?? f.replace(/_/g, " ");

/** "columns.<name>.<field>" → { column, field } */
function splitField(field: string): { column?: string; field: string } {
  if (field.startsWith("columns.")) {
    const rest = field.slice(8);
    const i = rest.lastIndexOf(".");
    // `columns.<name>` (added/removed column) has no sub-field.
    return i < 0 ? { column: rest, field: "(kolom)" } : { column: rest.slice(0, i), field: rest.slice(i + 1) };
  }
  return { field };
}

type Filter = "all" | "card" | "columns" | "structure";
/** Build bookkeeping rather than knowledge: collapsed by default and sorted last. */
const TECH_FIELDS = new Set(["review", "fingerprint", "enrichment", "models", "sources", "built_at", "verification", "profile_skipped", "profiled_at", "sampled_pct", "model", "confidence"]);
const PAGE = 40;

const cardCache = new Map<string, Promise<TableCard>>();

/** Pull one diff field (`purpose`, `columns.<name>.<field>`) out of a full card. */
function fieldValue(card: TableCard, field: string): unknown {
  const { column, field: f } = splitField(field);
  if (!column) return card[f];
  const cols = Array.isArray(card.columns) ? (card.columns as Record<string, unknown>[]) : [];
  const c = cols.find((x) => x && (x.name === column || x.column === column));
  return f === "(kolom)" ? c : c?.[f];
}

function Change({ e, mode, from, to, grouped }: { e: DiffEntry; mode: DiffMode; from: string; to: string; grouped?: boolean }) {
  const api = useContextApi();
  const k = kindOf(e);
  const { column, field } = splitField(e.field);
  const structural = e.before === "absent" || e.after === "absent" || e.before === "present" || e.after === "present";
  const truncated = [e.before, e.after].some((v) => typeof v === "string" && v.length >= 590);
  const [full, setFull] = useState<{ before: unknown; after: unknown } | null>(null);
  const [loadingFull, setLoadingFull] = useState(false);
  const [fullError, setFullError] = useState<string | null>(null);
  const loadFull = async () => {
    setLoadingFull(true);
    setFullError(null);
    try {
      const get = (v: string) => {
        const key = `${e.fqn}@${v}`;
        if (!cardCache.has(key)) cardCache.set(key, api.getTable(e.fqn, v));
        return cardCache.get(key)!;
      };
      const [a, b] = await Promise.all([get(from), get(to)]);
      setFull({ before: fieldValue(a, e.field), after: fieldValue(b, e.field) });
    } catch (err) {
      setFullError(contextErrorMessage(err));
    } finally {
      setLoadingFull(false);
    }
  };
  const before = full ? full.before : e.before;
  const after = full ? full.after : e.after;
  const body = <SmartDiff before={before} after={after} mode={mode} />;
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {column && !grouped && (
          <span className="font-mono text-[13px] font-semibold">
            {column}
            {field !== "(kolom)" && <span className="text-muted-foreground"> ›</span>}
          </span>
        )}
        {field !== "(kolom)" && <span className={cn("font-medium", !column && "text-[15px]")}>{fieldLabel(field)}</span>}
        <Badge tone={KIND_TONE[k]}>{structural ? (e.after === "present" ? (column ? "Kolom toegevoegd" : "Kaart toegevoegd") : column ? "Kolom verwijderd" : "Kaart verwijderd") : KIND_LABEL[k]}</Badge>
        {truncated && !full && !structural && (
          <button type="button" onClick={loadFull} disabled={loadingFull} className="text-[13px] font-medium text-info hover:underline disabled:opacity-60">
            {loadingFull ? "Volledige waarden laden…" : "Ingekort · volledige waarden laden"}
          </button>
        )}
        {full && <span className="text-xs text-muted-foreground">volledige waarden</span>}
      </div>
      {fullError && <p className="text-[13px] text-destructive">Volledige waarden niet geladen: {fullError}</p>}
      {!structural &&
        (TECH_FIELDS.has(field) ? (
          <details className="rounded-md border border-border">
            <summary className="cursor-pointer select-none px-3 py-1.5 text-[13px] text-muted-foreground hover:text-foreground">Technische metadata van het bouwproces — tonen</summary>
            <div className="border-t border-border p-2">{body}</div>
          </details>
        ) : (
          body
        ))}
    </div>
  );
}

export function DiffView({
  versions,
  from,
  to,
  onChange,
  onOpenTable,
}: {
  versions: Version[];
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
  onOpenTable?: (fqn: string) => void;
}) {
  const api = useContextApi();
  const handleError = useErrorHandler();
  const [entries, setEntries] = useState<DiffEntry[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tableQ, setTableQ] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [mode, setMode] = useState<DiffMode>("inline");
  const [selected, setSelected] = useState<string | null>(null);
  const [shown, setShown] = useState(PAGE);

  useEffect(() => {
    if (!from || !to || from === to) {
      setEntries(null);
      setError(null);
      return;
    }
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    api
      .diff(from, to, ac.signal)
      .then((d) => setEntries(Array.isArray(d) ? d : []))
      .catch((e) => {
        if (isAbortError(e)) return;
        setEntries(null);
        setError(handleError(e));
      })
      .finally(() => !ac.signal.aborted && setLoading(false));
    return () => ac.abort();
  }, [api, from, to, handleError]);

  const matches = (e: DiffEntry) => {
    if (filter === "card") return !e.field.startsWith("columns.");
    if (filter === "columns") return e.field.startsWith("columns.");
    if (filter === "structure") return [e.before, e.after].some((x) => x === "absent" || x === "present");
    return true;
  };

  const groups = useMemo(() => {
    const m = new Map<string, DiffEntry[]>();
    (entries ?? []).filter(matches).forEach((e) => {
      const l = m.get(e.fqn) ?? [];
      l.push(e);
      m.set(e.fqn, l);
    });
    return [...m.entries()].sort((a, b) => b[1].length - a[1].length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, filter]);

  const summary = useMemo(() => {
    const all = entries ?? [];
    return {
      total: all.length,
      tables: new Set(all.map((e) => e.fqn)).size,
      card: all.filter((e) => !e.field.startsWith("columns.")).length,
      columns: all.filter((e) => e.field.startsWith("columns.")).length,
      structure: all.filter((e) => [e.before, e.after].some((x) => x === "absent" || x === "present")).length,
    };
  }, [entries]);

  const shownGroups = tableQ ? groups.filter(([f]) => f.toLowerCase().includes(tableQ.toLowerCase())) : groups;
  const current0 = groups.find(([f]) => f === selected) ?? shownGroups[0];
  const current: [string, DiffEntry[]] | undefined = current0
    ? [current0[0], [...current0[1]].sort((a, b) => Number(TECH_FIELDS.has(splitField(a.field).field)) - Number(TECH_FIELDS.has(splitField(b.field).field)))]
    : undefined;
  useEffect(() => setShown(PAGE), [current?.[0], filter]);

  const vFrom = versions.find((v) => v.version === from);
  const vTo = versions.find((v) => v.version === to);
  const reversed = vFrom && vTo && new Date(vFrom.updated_at) > new Date(vTo.updated_at);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Direction controls */}
      <div className="border-b border-border bg-card px-5 py-3 lg:px-6">
        <div className="flex flex-col gap-2 xl:flex-row xl:items-end">
          <label className="min-w-0 flex-1">
            <span className="mb-1 block text-xs font-semibold text-muted-foreground">Basis (oud)</span>
            <Select value={from} onChange={(e) => onChange(e.target.value, to)} className="h-9" aria-label="Basisversie">
              <option value="">Kies een versie…</option>
              {versions.map((v) => (
                <option key={v.version} value={v.version}>
                  {versionLabel(v)}
                </option>
              ))}
            </Select>
          </label>
          <Button variant="ghost" size="icon" onClick={() => onChange(to, from)} aria-label="Richting omdraaien" title="Richting omdraaien" className="self-center xl:mb-0">
            <ArrowLeftRight className="h-4 w-4" />
          </Button>
          <label className="min-w-0 flex-1">
            <span className="mb-1 block text-xs font-semibold text-muted-foreground">Vergeleken met (nieuw)</span>
            <Select value={to} onChange={(e) => onChange(from, e.target.value)} className="h-9" aria-label="Doelversie">
              <option value="">Kies een versie…</option>
              {versions.map((v) => (
                <option key={v.version} value={v.version}>
                  {versionLabel(v)}
                </option>
              ))}
            </Select>
          </label>
        </div>
        {reversed && (
          <Alert tone="warning" className="mt-2 py-2">
            Let op: de basis is nieuwer dan de vergelijkingsversie. ‘Toegevoegd’ betekent hier dat iets in de oudere versie staat.{" "}
            <button type="button" className="font-medium underline" onClick={() => onChange(to, from)}>
              Omdraaien
            </button>
          </Alert>
        )}
        {entries && entries.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="mr-1 text-sm">
              <span className="font-semibold tabular-nums">{summary.total.toLocaleString("nl-NL")}</span> wijzigingen in{" "}
              <span className="font-semibold tabular-nums">{summary.tables}</span> tabellen
            </span>
            {(
              [
                ["all", `Alle`],
                ["card", `Kaartvelden ${summary.card.toLocaleString("nl-NL")}`],
                ["columns", `Kolommen ${summary.columns.toLocaleString("nl-NL")}`],
                ["structure", `Toegevoegd/verwijderd ${summary.structure}`],
              ] as [Filter, string][]
            ).map(([f, l]) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                aria-pressed={filter === f}
                className={cn(
                  "h-7 rounded-full border px-3 text-xs font-medium transition-colors",
                  filter === f ? "border-sl-charcoal bg-sl-charcoal text-white" : "border-border bg-card text-muted-foreground hover:text-foreground",
                )}
              >
                {l}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-1 rounded-md border border-border p-0.5" role="group" aria-label="Weergave">
              <button
                type="button"
                onClick={() => setMode("inline")}
                aria-pressed={mode === "inline"}
                className={cn("inline-flex h-7 items-center gap-1.5 rounded px-2 text-xs font-medium", mode === "inline" ? "bg-muted text-foreground" : "text-muted-foreground")}
              >
                <Rows3 className="h-3.5 w-3.5" />
                Samengevoegd
              </button>
              <button
                type="button"
                onClick={() => setMode("split")}
                aria-pressed={mode === "split"}
                className={cn("inline-flex h-7 items-center gap-1.5 rounded px-2 text-xs font-medium", mode === "split" ? "bg-muted text-foreground" : "text-muted-foreground")}
              >
                <Columns2 className="h-3.5 w-3.5" />
                Naast elkaar
              </button>
            </div>
          </div>
        )}
      </div>

      {from && to && from === to ? (
        <div className="p-6">
          <Alert tone="info">Kies twee verschillende versies om te vergelijken.</Alert>
        </div>
      ) : error ? (
        <div className="p-6">
          <Alert tone="danger">Vergelijking mislukt: {error}</Alert>
        </div>
      ) : loading ? (
        <div className="flex min-h-0 flex-1" role="status" aria-label="Verschillen laden">
          <div className="w-72 space-y-3 border-r border-border p-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-4 animate-pulse rounded bg-muted" />
            ))}
          </div>
          <div className="flex-1 space-y-4 p-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-20 animate-pulse rounded-md bg-muted/60" />
            ))}
          </div>
        </div>
      ) : entries && entries.length === 0 ? (
        <EmptyState icon={<GitCompare className="h-5 w-5" />} title="Geen verschillen" description="Deze twee versies hebben dezelfde inhoud (vluchtige buildvelden tellen niet mee)." />
      ) : entries ? (
        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          <aside className="flex max-h-64 min-h-0 shrink-0 flex-col border-b border-border md:max-h-none md:w-72 md:border-b-0 md:border-r">
            <div className="border-b border-border p-2.5">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input value={tableQ} onChange={(e) => setTableQ(e.target.value)} placeholder="Filter tabellen" className="h-8 pl-8 text-sm" aria-label="Gewijzigde tabellen filteren" />
              </div>
            </div>
            <ul className="min-h-0 flex-1 overflow-y-auto py-1" aria-label="Gewijzigde tabellen">
              {shownGroups.map(([fqn, rows]) => (
                <li key={fqn}>
                  <button
                    type="button"
                    onClick={() => setSelected(fqn)}
                    aria-current={current?.[0] === fqn || undefined}
                    className={cn(
                      "flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] hover:bg-sl-grey-50",
                      current?.[0] === fqn && "bg-accent shadow-[inset_3px_0_0_var(--color-sl-yellow)]",
                    )}
                    title={fqn}
                  >
                    <span className="min-w-0 flex-1 truncate font-mono">{fqn.split(".").pop()}</span>
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{rows.length}</span>
                  </button>
                </li>
              ))}
              {!shownGroups.length && <li className="px-3 py-4 text-sm text-muted-foreground">Geen tabellen voor dit filter.</li>}
            </ul>
          </aside>
          <section className="min-h-0 min-w-0 flex-1 overflow-y-auto">
            {current ? (
              <div className="px-5 py-5 lg:px-6">
                <div className="mb-4 flex flex-wrap items-center gap-3">
                  <h3 className="min-w-0 break-all font-mono text-lg font-semibold">{current[0]}</h3>
                  <span className="text-sm tabular-nums text-muted-foreground">{current[1].length} wijzigingen</span>
                  {onOpenTable && (
                    <Button variant="ghost" size="sm" onClick={() => onOpenTable(current[0])}>
                      <Table2 className="h-4 w-4" />
                      Open kaart
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
                <div className="mb-4 flex items-center gap-4 text-xs text-muted-foreground">
                  <span>
                    Legenda: <del className="rounded-sm bg-[#FBD9DB] px-1 text-[#7A0A10]">verwijderd</del> <ins className="rounded-sm bg-[#D7EBC2] px-1 text-[#1F3A06] no-underline">toegevoegd</ins>
                  </span>
                  <span>Lange waarden komen ingekort binnen; gebruik “volledige waarden laden” bij een wijziging om ze compleet te vergelijken.</span>
                </div>
                <GroupedChanges entries={current[1].slice(0, shown)} mode={mode} from={from} to={to} />
                {current[1].length > shown && (
                  <div className="mt-6 flex justify-center">
                    <Button variant="outline" size="sm" onClick={() => setShown((s) => s + PAGE)}>
                      Toon meer ({current[1].length - shown} resterend)
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <EmptyState title="Geen wijzigingen voor dit filter" />
            )}
          </section>
        </div>
      ) : (
        <EmptyState icon={<GitCompare className="h-5 w-5" />} title="Kies twee versies" description="Selecteer een basis en een versie om mee te vergelijken." />
      )}
    </div>
  );
}

/** Card fields first, then column changes grouped under one heading per column. */
function GroupedChanges({ entries, mode, from, to }: { entries: DiffEntry[]; mode: DiffMode; from: string; to: string }) {
  const cardFields = entries.filter((e) => !splitField(e.field).column);
  const cols = new Map<string, DiffEntry[]>();
  entries
    .filter((e) => splitField(e.field).column)
    .forEach((e) => {
      const c = splitField(e.field).column!;
      cols.set(c, [...(cols.get(c) ?? []), e]);
    });
  return (
    <div className="space-y-8">
      {cardFields.length > 0 && (
        <section className="space-y-5">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Kaartvelden ({cardFields.length})</h4>
          {cardFields.map((e, i) => (
            <Change key={`${e.field}-${i}`} e={e} mode={mode} from={from} to={to} />
          ))}
        </section>
      )}
      {cols.size > 0 && (
        <section className="space-y-5">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Kolommen ({cols.size})</h4>
          {[...cols.entries()].map(([c, list]) => (
            <div key={c} className="rounded-lg border border-border">
              <div className="border-b border-border bg-sl-grey-50 px-3 py-2 font-mono text-sm font-semibold">{c}</div>
              <div className="space-y-4 p-3">
                {list.map((e, i) => (
                  <Change key={`${e.field}-${i}`} e={e} mode={mode} from={from} to={to} grouped />
                ))}
              </div>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
